import { Tabs, Box, Button, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { SelectColumn } from 'react-data-grid';

import {
  createUSInvoiceReports,
  fetchUSInvoicedPurchaseOrders,
  fetchUSInvoicePurchaseOrders,
} from '../../../actions/actions_us_invoicing';
import { downloadFromS3Link } from '../../../helpers/downloads';
import Loading from '../../basic/Loading';
import Constants from '../../../Constants';
import { FinanceNavMainTabs, ResultTab } from '../FinanceNavMainTabs';
import DashboardSectionTile from '../../core/DashboardSectionTile';
import USInvoicingFilters from './USInvoicingFilters/USInvoicingFilters';
import USInvoicingTable from './USInvoicingTable/USInvoicingTable';
import { fetchConfigs } from '../../../actions/actions_config';
import { getInternalPoLabel } from '../../../utils';
import './Index.scss';

const TABS = {
  generateReport: 'Generate Report',
  generatedReports: 'Generated Reports',
};

const TABS_LIST = [TABS.generateReport, TABS.generatedReports];

const DEFAULT_ACTIVE_TAB = TABS.generateReport;

class USInvoicing extends Component {
  static propTypes = {
    token: PropTypes.string,
    dispatch: PropTypes.func,
    usInvoiceHistoricalData: PropTypes.shape({
      orders: PropTypes.arrayOf(PropTypes.object),
      pagination: PropTypes.shape({
        pages: PropTypes.number,
        has_next: PropTypes.bool,
        has_previous: PropTypes.bool,
        next_page: PropTypes.number,
        previous_page: PropTypes.number,
        total: PropTypes.number,
      }),
      pageCount: PropTypes.number,
    }),
    ordersToInvoice: PropTypes.arrayOf(PropTypes.object),
    isSearching: PropTypes.bool,
    configs: PropTypes.arrayOf(PropTypes.object),
  };

  state = {
    selectedTab: DEFAULT_ACTIVE_TAB,
    grinderReceivedStartDate: '',
    grinderReceivedEndDate: '',
    invoicingOffice: Constants.BUSINESS_OFFICES.CHICAGO.value,
    generationColumnList: [
      SelectColumn,
      {
        key: 'internal_po_number',
        name: 'internal_po_number',
      },
      {
        key: 'grinder_po_number',
        name: 'Grinder PO #',
      },
      {
        key: 'packer_plant_name',
        name: 'Establishment',
      },
      {
        key: 'packer_invoice_number',
        name: 'Packer Invoice #',
      },
      {
        key: 'input_product',
        name: 'Product',
      },
      {
        key: 'buy_price_per_unit',
        name: 'Buy Price Per Unit',
      },
      {
        key: 'testing_fee',
        name: 'Testing Fee',
      },
      {
        key: 'group_housed_premium_rate',
        name: 'GHP',
      },
      {
        key: 'recv_quantity',
        name: 'Grinder Received Weights',
      },
      {
        key: 'packer_shipped_weight',
        name: 'Packer Shipped Weights',
      },
      {
        key: 'recv_grinder_date',
        name: 'Grinder Received Date',
      },
      {
        key: 'invoicing_office',
        name: 'Invoicing Office',
      },
    ],
    selectedRowsList: [],
    isLoading: false,
    generatedColumnList: [
      {
        key: 'created',
        name: 'Created On',
        formatFunc: 'formatDate',
      },
      {
        name: 'Date from',
        key: 'ready_for_payment_start_date',
      },
      {
        name: 'Date to',
        key: 'ready_for_payment_end_date',
      },
      {
        key: 'invoicing_office',
        name: 'Invoicing Office',
      },
      {
        key: 'user_name',
        name: 'Created By',
      },
      {
        key: 'download_url',
        name: 'Report',
        formatFunc: 'download',
      },
    ],
    pageNum: 1,
  };

  componentDidMount() {
    const { token, dispatch, configs } = this.props;
    dispatch(
      fetchUSInvoicedPurchaseOrders(token, {
        pageNum: this.state.pageNum,
      })
    );
    if (configs.length === 0) {
      dispatch(fetchConfigs(token));
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { token, dispatch, generatedUSInvoiceLink } = this.props;

    if (generatedUSInvoiceLink && generatedUSInvoiceLink !== prevProps.generatedUSInvoiceLink) {
      downloadFromS3Link(generatedUSInvoiceLink.url);
      this.setState({ isLoading: false });
      // if new reports generated, update generated section
      dispatch(
        fetchUSInvoicedPurchaseOrders(token, {
          pageNum: 1,
        })
      );
    }
    // when page changes
    if (prevState.pageNum !== this.state.pageNum) {
      dispatch(
        fetchUSInvoicedPurchaseOrders(token, {
          pageNum: this.state.pageNum,
        })
      );
    }
  }

  handleTabChange = selectedTab => {
    const tabName = TABS_LIST[selectedTab];
    this.setState({
      selectedTab: tabName,
    });
  };

  onChangeFilterValue = (field, value) => {
    this.setState({
      [field]: value,
    });
  };

  handlePageChange = ({ selected }) => {
    const pageNum = selected + 1;
    this.setState({ pageNum });
  };

  fetchUSInvoicingOrders = () => {
    const { grinderReceivedStartDate, grinderReceivedEndDate, invoicingOffice } = this.state;
    const { token, dispatch } = this.props;
    dispatch(
      fetchUSInvoicePurchaseOrders(token, {
        grinderReceivedStartDate,
        grinderReceivedEndDate,
        invoicingOffice,
      })
    );
  };

  handleRowSelect = selectedOrdersSet => {
    const { ordersToInvoice } = this.props;

    const selectedRowsList = ordersToInvoice.filter(row =>
      [...selectedOrdersSet].includes(`${row.internal_po_number}${row.grinder_po_number}`)
    );
    this.setState({
      selectedRowsList,
    });
  };

  handleSubmit = () => {
    const { token, dispatch } = this.props;
    const { grinderReceivedStartDate, grinderReceivedEndDate, invoicingOffice } = this.state;
    const { selectedRowsList } = this.state;

    const grinderPOs = selectedRowsList.map(row => row.grinder_po_number).filter(g => g !== null || g !== '');
    const fmgPOs = selectedRowsList.map(row => row.internal_po_number).filter(f => f !== null);

    const filters = {
      grinderReceivedStartDate,
      grinderReceivedEndDate,
      invoicingOffice,
    };

    this.setState({ isLoading: true }, () => {
      dispatch(
        createUSInvoiceReports(token, {
          grinderPOs,
          filters,
          fmgPOs,
        })
      );
    });
  };

  setInternalPoLabel = label => {
    if (!label) {
      return null;
    }
    if (
      this.state.generationColumnList[1].name !== 'internal_po_number' ||
      this.state.generationColumnList[1].name === label
    ) {
      return null;
    }
    const copy = this.state.generationColumnList;
    copy[1].name = label;
    this.setState({ generationColumnList: copy });
  };

  render() {
    const { selectedTab, selectedRowsList, generationColumnList, generatedColumnList, isLoading } = this.state;
    const { ordersToInvoice, usInvoiceHistoricalData, isSearching, configs } = this.props;
    const internal_po_label = getInternalPoLabel(configs);
    this.setInternalPoLabel(internal_po_label);
    
    // Debug logging
    console.log('US Invoicing - usInvoiceHistoricalData:', usInvoiceHistoricalData);
    
    return (
      <VStack gap="56px" align="stretch" marginBottom="95px">
        <FinanceNavMainTabs activeTabIndex={2} />
        <VStack align="stretch" paddingX="51px" gap="37px">
          <Tabs.Root
            defaultValue="Generate Report"
            defaultActiveKey={TABS.results}
            activeKey={selectedTab}
            onChange={this.handleTabChange}
          >
            <Tabs.List width="max-content" borderBottomWidth="0px">
              <ResultTab value={TABS.generateReport} key={TABS.generateReport} title="Generate Report" />
              <ResultTab value={TABS.generatedReports} key={TABS.generatedReports} title="Generated Reports" />
            </Tabs.List>

            <Tabs.Content value={TABS.generateReport} tab="Generate Report" key={TABS.generateReport} padding="0px">
              <VStack gap="37px" align="stretch">
                <DashboardSectionTile
                  borderTopLeftRadius="0px"
                  title="1. Filter Pos to Generate Report From"
                  minWidth="1266px"
                >
                  {/* US Invoicing specific filters */}
                  <USInvoicingFilters
                    isSearching={isSearching}
                    onChangeFilter={this.onChangeFilterValue}
                    {...this.state}
                    onSearch={this.fetchUSInvoicingOrders}
                  />
                </DashboardSectionTile>

                {ordersToInvoice && ordersToInvoice.length > 0 && (
                  <DashboardSectionTile title="2. Select Pos for US Invoice Generation" minWidth="1266px">
                    <Box key="finance-page-us-invoicing-generate__grid-results">
                      <Box>
                        <USInvoicingTable
                          ordersToDrawDown={ordersToInvoice}
                          selectedRows={selectedRowsList}
                          columnList={generationColumnList}
                          onRowSelect={this.handleRowSelect}
                          tableInfo="Choose which items to generate a report from"
                          displayTotalsText
                        />
                      </Box>
                    </Box>
                  </DashboardSectionTile>
                )}

                <Box marginLeft="auto !important">
                  {isLoading || isSearching ? (
                    <Loading style={{ width: 'max-content', height: 'fit-content', padding: '0px' }} />
                  ) : (
                    <Button
                      onClick={this.handleSubmit}
                      disabled={!ordersToInvoice || ordersToInvoice.length === 0}
                      colorScheme="actionPrimary"
                      width="278px"
                      height="40px"
                      fontWeight="black"
                      fontSize="16px"
                    >
                      US Invoice Report(s) To NetSuite
                    </Button>
                  )}
                </Box>
              </VStack>
            </Tabs.Content>
            
            {/* Generated reports  */}
            <Tabs.Content
              value={TABS.generatedReports}
              tab="Generated Reports"
              key={TABS.generatedReports}
              padding="0px"
            >
              <DashboardSectionTile borderTopLeftRadius="0px" minWidth="1266px">
                {usInvoiceHistoricalData && usInvoiceHistoricalData.orders && (
                  <USInvoicingTable
                    ordersToDrawDown={usInvoiceHistoricalData.orders || []}
                    columnList={generatedColumnList}
                    pagination={usInvoiceHistoricalData.pagination}
                    onPageChange={this.handlePageChange}
                    displayTotalsText={false}
                  />
                )}
              </DashboardSectionTile>
            </Tabs.Content>
          </Tabs.Root>
        </VStack>
      </VStack>
    );
  }
}

export default USInvoicing;